import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Trash2, Plus, Wallet } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { EXP_STATUS, EXP_TYPES } from "./eventosUtils";
import { ReadinessBadge, ReadinessListe } from "./Readiness";

const Seksjon = ({ label, children }) => (
  <div className="mt-6">
    <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-neutral-400 mb-2">{label}</p>
    {children}
  </div>
);

const Felt = ({ label, verdi, onLagre, testid, textarea, type = "text" }) => {
  const [v, setV] = useState(verdi ?? "");
  useEffect(() => { setV(verdi ?? ""); }, [verdi]);
  const Tag = textarea ? "textarea" : "input";
  return (
    <label className="adm-label">
      {label}
      <Tag
        value={v}
        type={textarea ? undefined : type}
        rows={textarea ? 3 : undefined}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => String(v) !== String(verdi ?? "") && onLagre(type === "number" ? Number(v) || 0 : v)}
        className="adm-input"
        data-testid={testid}
      />
    </label>
  );
};

export default function ExperienceDrawer({ exp, spaces, sponsors, budgetLines, members, aktor, onPatch, onReload, onDelete, onLukk }) {
  const [saker, setSaker] = useState(null);
  const [nySak, setNySak] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onLukk(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onLukk]);

  useEffect(() => {
    let aktiv = true;
    api.get("/admin/tasks").then(({ data }) => {
      if (!aktiv) return;
      setSaker((data.tasks || []).filter((t) => (t.linkedEntities || []).some((le) => le.type === "experience" && le.id === exp.id)));
    }).catch(() => setSaker([]));
    return () => { aktiv = false; };
  }, [exp.id]);

  const st = EXP_STATUS.find((s) => s.k === exp.status) || EXP_STATUS[0];
  const koblet = (budgetLines || []).find((l) => l.id === exp.budgetLineId);

  const lagNySak = async () => {
    const t = nySak.trim();
    if (!t) return;
    try {
      const { data } = await api.post("/admin/tasks", {
        title: t,
        actor: aktor,
        linkedEntities: [{ type: "experience", id: exp.id, label: exp.name }],
      });
      setSaker((prev) => [data.task, ...(prev || [])]);
      setNySak("");
      toast.success("Sak opprettet og koblet");
    } catch (e) {
      toast.error("Kunne ikke opprette sak");
    }
  };

  const lagBudsjettlinje = async () => {
    try {
      await api.post(`/admin/os/experiences/${exp.id}/budget-line`, { actor: aktor });
      toast.success("Budsjettlinje opprettet og koblet");
      await onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke opprette budsjettlinje");
    }
  };

  const aktivitet = [...(exp.activity || [])].reverse();

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="experience-drawer">
      <div className="sak-backdrop absolute inset-0 bg-black/25" onClick={onLukk} />
      <aside className="sak-drawer relative w-full max-w-[600px] h-full bg-white shadow-2xl border-l border-[#E7E4DB] flex flex-col">
        <div className="px-6 pt-5 pb-4 border-b border-neutral-100 shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-[19px] font-semibold leading-tight" data-testid="experience-drawer-navn">{exp.name}</h2>
              <p className="text-[12px] text-neutral-400 mt-0.5">
                {[exp.start && exp.end ? `${exp.start}–${exp.end}` : null, (spaces || []).find((s) => s.id === exp.spaceId)?.name].filter(Boolean).join(" · ") || "Tid og rom ikke satt"}
              </p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button type="button" onClick={onDelete} className="p-1.5 text-neutral-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" data-testid="experience-slett-btn">
                <Trash2 size={15} />
              </button>
              <button type="button" onClick={onLukk} className="p-1.5 text-neutral-400 hover:text-neutral-800 hover:bg-neutral-100 rounded-md transition-colors" data-testid="experience-drawer-lukk">
                <X size={17} />
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3 mt-3">
            <select
              value={exp.status}
              onChange={(e) => onPatch({ status: e.target.value })}
              className="text-[12.5px] font-medium border rounded-lg px-2.5 py-1.5 bg-white focus:outline-none transition-colors"
              style={{ borderColor: st.dot, color: st.dot }}
              data-testid="experience-status-select"
            >
              {EXP_STATUS.map((s) => (
                <option key={s.k} value={s.k}>{s.label}</option>
              ))}
            </select>
            <ReadinessBadge computed={exp.computed} size="md" />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 pb-6">
          <Seksjon label="Readiness">
            <ReadinessListe computed={exp.computed} onSett={(r) => onPatch({ readiness: r })} testidPrefix="experience" />
          </Seksjon>

          <Seksjon label="Detaljer">
            <div className="grid grid-cols-2 gap-3">
              <Felt label="Navn" verdi={exp.name} onLagre={(v) => onPatch({ name: v })} testid="experience-navn" />
              <label className="adm-label">
                Type
                <select value={exp.type} onChange={(e) => onPatch({ type: e.target.value })} className="adm-input" data-testid="experience-type-select">
                  {EXP_TYPES.map((t) => (
                    <option key={t.k} value={t.k}>{t.label}</option>
                  ))}
                </select>
              </label>
              <label className="adm-label">
                Start
                <input
                  type="time"
                  defaultValue={exp.start || ""}
                  onBlur={(e) => e.target.value !== (exp.start || "") && onPatch({ start: e.target.value })}
                  className="adm-input"
                  data-testid="experience-start"
                />
              </label>
              <label className="adm-label">
                Slutt
                <input
                  type="time"
                  defaultValue={exp.end || ""}
                  onBlur={(e) => e.target.value !== (exp.end || "") && onPatch({ end: e.target.value })}
                  className="adm-input"
                  data-testid="experience-slutt"
                />
              </label>
              <label className="adm-label">
                Rom
                <select value={exp.spaceId || ""} onChange={(e) => onPatch({ spaceId: e.target.value || null })} className="adm-input" data-testid="experience-space-select">
                  <option value="">Ikke satt</option>
                  {(spaces || []).map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </label>
              <Felt label="Kapasitet" verdi={exp.capacity} type="number" onLagre={(v) => onPatch({ capacity: v })} testid="experience-kapasitet" />
              <label className="adm-label">
                Eier
                <select value={exp.ownerId || ""} onChange={(e) => onPatch({ ownerId: e.target.value || null })} className="adm-input" data-testid="experience-owner-select">
                  <option value="">Ingen</option>
                  {(members || []).map((m) => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              </label>
              <Felt label="Leverandør (fritekst)" verdi={exp.supplier} onLagre={(v) => onPatch({ supplier: v })} testid="experience-leverandor" />
              <div className="col-span-2">
                <Felt label="Beskrivelse" verdi={exp.description} onLagre={(v) => onPatch({ description: v })} testid="experience-beskrivelse" textarea />
              </div>
            </div>
          </Seksjon>

          <Seksjon label="Koblinger">
            <div className="grid grid-cols-2 gap-3">
              <label className="adm-label">
                Partneravtale
                <select value={exp.agreementId || ""} onChange={(e) => onPatch({ agreementId: e.target.value || null })} className="adm-input" data-testid="experience-avtale-select">
                  <option value="">Ingen</option>
                  {(sponsors || []).map((a) => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
              </label>
              <label className="adm-label">
                Budsjettlinje
                <select value={exp.budgetLineId || ""} onChange={(e) => onPatch({ budgetLineId: e.target.value || null })} className="adm-input" data-testid="experience-budsjettlinje-select">
                  <option value="">Ingen</option>
                  {(budgetLines || []).map((l) => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
              </label>
            </div>
            {koblet ? (
              <button
                type="button"
                onClick={() => nav("/admin/budsjett")}
                className="mt-2 text-[12px] text-neutral-600 underline underline-offset-2 hover:text-neutral-900 transition-colors"
                data-testid="experience-til-budsjett"
              >
                Koblet til «{koblet.name}» — åpne budsjett →
              </button>
            ) : (
              <button type="button" onClick={lagBudsjettlinje} className="adm-btn-ghost adm-btn-sm mt-2" data-testid="experience-lag-budsjettlinje">
                <Wallet size={12} /> Opprett budsjettlinje for denne experiencen
              </button>
            )}
          </Seksjon>

          <Seksjon label="Interne notater">
            <Felt label="" verdi={exp.notes} onLagre={(v) => onPatch({ notes: v })} testid="experience-notater" textarea />
          </Seksjon>

          <Seksjon label={`Saker (${saker == null ? "…" : saker.length})`}>
            {(saker || []).map((t) => (
              <p key={t.id} className="flex items-center gap-2 text-[12.5px] py-1" data-testid={`experience-sak-${t.id}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${t.status === "done" ? "bg-emerald-500" : "bg-amber-500"}`} />
                <span className={t.status === "done" ? "text-neutral-400 line-through" : "text-neutral-700"}>{t.title}</span>
                {t.dueDate && <span className="text-[10.5px] text-neutral-400 ml-auto">{t.dueDate}</span>}
              </p>
            ))}
            <div className="flex items-center gap-2 mt-1.5">
              <Plus size={13} className="text-neutral-300 shrink-0" />
              <input
                value={nySak}
                onChange={(e) => setNySak(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); lagNySak(); } }}
                placeholder="Ny sak koblet til denne experiencen — Enter"
                className="flex-1 text-[12.5px] py-1 bg-transparent text-neutral-900 focus:outline-none placeholder:text-neutral-300"
                data-testid="experience-ny-sak-input"
              />
            </div>
          </Seksjon>

          {aktivitet.length > 0 && (
            <Seksjon label="Aktivitet">
              <div className="space-y-1">
                {aktivitet.slice(0, 8).map((a, i) => (
                  <p key={i} className="text-[11px] text-neutral-400 leading-relaxed">
                    <span className="text-neutral-600 font-medium">{a.actor}</span> · {a.text}
                    <span className="ml-1.5 text-neutral-300">{new Date(a.at).toLocaleDateString("nb-NO", { day: "numeric", month: "short" })}</span>
                  </p>
                ))}
              </div>
            </Seksjon>
          )}
        </div>
      </aside>
    </div>
  );
}
